/**
 * NotificationBell — Sprint 82+ : cloche unifiée dans le header AppShell.
 * Badge rouge du nombre de non-lues + dropdown des 5 dernières + "Voir toutes".
 * Poll toutes les 60s.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Bell, BellRing, AlertTriangle, AlertCircle, Check } from "lucide-react";

const SEV_ICON = {
  info: Bell,
  warning: AlertTriangle,
  critical: AlertCircle,
};
const SEV_COLOR = {
  info: "text-blue-600",
  warning: "text-amber-600",
  critical: "text-rose-600",
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [list, count] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems((list.data.notifications || []).slice(0, 5));
      setUnread(count.data.unread || 0);
    } catch { /* silent — feature transverse, pas de toast */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  const readOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch { /* silent */ }
  };
  const markAll = async () => {
    try { await api.post("/notifications/mark-all-read"); load(); }
    catch { /* silent */ }
  };
  const openItem = (n) => {
    if (!n.read) readOne(n.id);
    setOpen(false);
    if (n.action_url) navigate(n.action_url);
    else navigate("/app/notifications");
  };

  const HasNew = unread > 0;
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
              className="relative p-2 rounded-full hover:bg-slate-100 transition"
              data-testid="notification-bell">
        {HasNew ? <BellRing className="h-5 w-5 text-blue-600" />
                : <Bell className="h-5 w-5 text-slate-500" />}
        {HasNew && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border z-50 overflow-hidden"
              data-testid="notification-dropdown">
              <div className="flex items-center justify-between p-3 border-b bg-slate-50">
                <p className="text-sm font-semibold">
                  Notifications {HasNew && <span className="text-rose-500">({unread})</span>}
                </p>
                {HasNew && (
                  <button onClick={markAll}
                          className="text-xs text-blue-600 hover:underline"
                          data-testid="mark-all-notif-btn">
                    <Check className="w-3 h-3 inline mr-1" />Tout lu
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {items.length === 0 ? (
                  <p className="p-6 text-center text-sm text-slate-500">
                    Aucune notification 👍
                  </p>
                ) : items.map((n) => {
                  const Icon = SEV_ICON[n.severity] || Bell;
                  return (
                    <button key={n.id}
                            onClick={() => openItem(n)}
                            className={`w-full text-left p-3 border-b hover:bg-slate-50 flex gap-2 ${!n.read ? "bg-blue-50/40" : ""}`}
                            data-testid={`notif-item-${n.id}`}>
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${SEV_COLOR[n.severity] || "text-slate-500"}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${!n.read ? "font-semibold" : ""}`}>
                          {n.title}
                        </p>
                        <p className="text-xs text-slate-500 line-clamp-2">{n.body}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {new Date(n.created_at).toLocaleString("fr-FR", {
                            day: "2-digit", month: "short",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </p>
                      </div>
                      {!n.read && <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <button onClick={() => { setOpen(false); navigate("/app/notifications"); }}
                      className="w-full p-2 text-sm text-blue-600 hover:bg-slate-50 border-t font-medium"
                      data-testid="view-all-notif-btn">
                Voir toutes les notifications →
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
